import * as React from 'react';
import { getFormatDateFromStoreDate } from 'shared-components';
import { Section } from '../../../../Finger/Section';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../../utilities/documents';
import FingerWithApprove from '../../../../atoms/finger-with-approve';
import NoDocuments from '../../../../atoms/no-doc-placeholder';

import { UserRole } from '../../../../../store/auth/types';
import { SummaryRow } from '../../../../Summary/SummaryRow';
import { DocAccessType, IFollowUpDocuments, StudentRefusalLetterDocList } from '../../../../../types';
import { boolToYesNo } from '../../../../../utilities/formatting';
import { AppliedAdminReviewStatus, RefusalGrounds } from 'types';
import { useTranslation } from "react-i18next";

interface IProps {
  userAccess?: DocAccessType;
  documents: IFollowUpDocuments['beforeTravel']['studentRefusalLetter'];
  userRole?: UserRole;

  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;

  onUploadClick: (docKey: string) => void;
  onOpenClick: (docKey: string) => void;
  onApproveClick: (docKey: string) => void;
  onRejectClick: (docKey: string) => void;
  onRescindRejectClick: (docKey: string) => void;
  onDeleteClick: (docKey: string) => void;
  onAcknowledgeClick: (docKey: string) => void;
}

const getRefusalGroups = (groups: RefusalGrounds[]) => {
  return groups
    ? groups.map(item => {
        return (
          <span key={item}>
            {item === RefusalGrounds.FINANCE
              ? 'Finance'
              : item === RefusalGrounds.ENGLISH
                ? 'English'
                : item === RefusalGrounds.CREDIBILITY
                  ? 'Credibility'
                  : item === RefusalGrounds.CAS
                    ? 'CAS'
                    : item === RefusalGrounds.COURSE_OR_QUALIFICATIONS
                      ? 'Course or qualifications'
                      : item}
            <br />
          </span>
        );
      })
    : null;
};

const StudentRefusalLetter = (props: IProps) => {
    const { t } = useTranslation('common');
  const { documents: studentRefusalLetterDocs, userAccess, userRole } = props;

  const visibleDocs = getVisible({ documentsHash: studentRefusalLetterDocs, userAccess, userRole });

  const hasDocs = visibleDocs.length > 0;
  const hasDeletedDocs = hasDeleted(studentRefusalLetterDocs);

  const docNodes: React.ReactNode[] = [];

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if (userAccess && !hasDocs) {
    return null;
  }

  if (hasDocs) {
    docNodes.push(
      ...visibleDocs.map((refusalLetter: StudentRefusalLetterDocList, refusalLetterIndex: number) => {
        const { key: docKey, item } = refusalLetter;

        return (
          <FingerWithApprove
            id={`student-refusal-letter-${docKey}`}
            key={`student-refusal-letter-${docKey}`}
            status={getFingerStatus(item)}
            isProcessingPdf={item.isProcessingPdf}
            reason={item.reason}
            fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
            fileNames={item.fileNames}
            label={t('common:visaRefusalLetter', { refusalLetterIndex: Object.keys(refusalLetter ?? {}).length > 1 ? (refusalLetterIndex > 0 ? refusalLetterIndex + 1 : '') : '' })}
            canUpload={props.canUpload}
            canOpen={props.canOpen}
            canApprove={props.canApprove}
            canReject={props.canReject}
            isDeleting={props.isDeleting}
            canAcknowledge={false}
            onUploadClick={() => {
              props.onUploadClick(docKey);
            }}
            onOpenClick={() => {
              props.onOpenClick(docKey);
            }}
            onApproveClick={() => {
              props.onApproveClick(docKey);
            }}
            onRejectClick={() => {
              props.onRejectClick(docKey);
            }}
            onRescindRejectClick={() => {
              props.onRescindRejectClick(docKey);
            }}
            onDeleteClick={() => {
              props.onDeleteClick(docKey);
            }}
            onAcknowledgeClick={() => {}}
            userRole={props.userRole}
            deletedByTTL={item.deletedByTTL}
          >
            <SummaryRow
              key={`before-travel-refusal-date-${docKey}`}
              label={t('common:refusalDate')}
              fullLabel="What is the date of the refusal?"
              value={getFormatDateFromStoreDate(item.refusalDate)}
            />

            <SummaryRow
              key={`before-travel-application-date-${docKey}`}
              label={t('common:applicationDate')}
              fullLabel="When did you submit your visa application?"
              value={getFormatDateFromStoreDate(item.applicationDate)}
            />

            <SummaryRow
              key={`before-travel-attended-interview-${docKey}`}
              label={t('common:attendedInterview')}
              fullLabel="Did you attend an interview with a UKVI immigration officer as part of your student visa application process?"
              value={boolToYesNo(item.attendedInterview)}
            />

            {item.refusalGrounds && item.refusalGrounds.length > 0 && (
              <SummaryRow
                key={`before-travel-refusal-grounds-${docKey}`}
                label={t('common:refusalGrounds')}
                fullLabel="Which of these best describe the reason for your visa refusal?"
                value={getRefusalGroups(item.refusalGrounds)}
              />
            )}

            <SummaryRow
              key={`before-travel-refusal-grounds-info-${docKey}`}
              label={t('common:refusalGroundsInfo')}
              fullLabel="Please describe in your own words why you feel your visa was refused"
              value={item.refusalGroundsInfo}
            />

            <SummaryRow
              key={`before-travel-applied-for-admin-review-${docKey}`}
              label={t('common:appliedForAdminReview')}
              fullLabel="Have you already applied for administrative review?"
              value={item.appliedForAdminReview}
            />

            {item.appliedForAdminReview === AppliedAdminReviewStatus.YES && (
              <SummaryRow
                key={`before-travel-admin-review-date-${docKey}`}
                label={t('common:adminReviewDate')}
                fullLabel="When did you apply for an administrative review?"
                value={getFormatDateFromStoreDate(item.adminReviewDate)}
              />
            )}
          </FingerWithApprove>
        );
      }),
    );
  }

  return (
    <>
      <Section
        key={`before-travel`}
        label={t('common:uploadYourVisaRefusalLetter')}
        canEdit={false}
        edit={() => true}
        warning={''}
      >
        {hasDocs ? docNodes : <NoDocuments />}
      </Section>
    </>
  );
};

export default StudentRefusalLetter;
